import { findGitRoot } from './config-as-code/workspace.js';
import { readRootConfig } from './config-as-code/root-config.js';

/**
 * Resolves which organization the CLI operates against (sent as the `X-Rekor-Active-Org`
 * header). Precedence, highest first:
 *   1. `--org` global flag (stashed here by the program's preAction hook)
 *   2. `REKOR_ORG` env var (CI / headless override)
 *   3. `.rekor.yaml` at the git root (written by `rekor init`)
 * The header is membership-validated server-side and ignored for `rec_` tokens (their org is
 * bound into the token), so an unset/spoofed value can't escalate — it just falls back to the
 * backend's primary org. Re-reads on each call; the CLI is a one-shot process.
 */

let cliOrgOverride: string | undefined;

/** Stash the `--org` flag value (called from the program's preAction hook). */
export function setCliOrgOverride(value: string | undefined): void {
  cliOrgOverride = value;
}

export interface ActiveOrg {
  orgId?: string;
  source?: 'flag' | 'env' | 'file';
}

const SOURCE_LABEL: Record<NonNullable<ActiveOrg['source']>, string> = {
  flag: '--org',
  env: 'REKOR_ORG',
  file: '.rekor.yaml',
};

/** Human-friendly label for where the active org came from (e.g. `.rekor.yaml`); null when unknown. */
export function activeOrgSourceLabel(source: ActiveOrg['source']): string | null {
  return source ? SOURCE_LABEL[source] : null;
}

export function resolveActiveOrg(): ActiveOrg {
  if (cliOrgOverride) return { orgId: cliOrgOverride, source: 'flag' };

  const env = process.env['REKOR_ORG'];
  if (env) return { orgId: env, source: 'env' };

  const gitRoot = findGitRoot();
  if (gitRoot) {
    const root = readRootConfig(gitRoot);
    if (root) return { orgId: root.org_id, source: 'file' };
  }

  return {};
}
